import { adminAxiosInstance } from "./adminAxiosInstance";
import { storeAdminUserSnapshot } from "./adminMarketingUsers";
const OPS_ORDERS_FRESH_TTL = 2 * 60 * 1000;
const OPS_ORDERS_STALE_TTL = 10 * 60 * 1000;
const OPS_ORDER_SESSION_KEY = "agrogrid_admin_ops_order_snapshots";
const OPS_ORDER_SESSION_LIMIT = 30;
const opsOrdersCache = new Map();
const opsOrdersRequests = new Map();
let latestOpsCampaigns = [];

const readOpsOrderSnapshots = () => {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(OPS_ORDER_SESSION_KEY)) || {};
  } catch {
    window.sessionStorage.removeItem(OPS_ORDER_SESSION_KEY);
    return {};
  }
};

const storeOpsOrderSnapshot = (order) => {
  if (typeof window === "undefined" || !order?.order_id) return;
  try {
    const snapshots = readOpsOrderSnapshots();
    snapshots[order.order_id] = { order, savedAt: Date.now() };
    const trimmedSnapshots = Object.fromEntries(
      Object.entries(snapshots)
        .sort(([, first], [, second]) => second.savedAt - first.savedAt)
        .slice(0, OPS_ORDER_SESSION_LIMIT)
    );
    window.sessionStorage.setItem(OPS_ORDER_SESSION_KEY, JSON.stringify(trimmedSnapshots));
  } catch {
  }
};

const readOpsOrderSnapshot = (orderId) => readOpsOrderSnapshots()[orderId]?.order || null;

const getOpsOrdersParams = ({ page = 1, pageSize = 10, period, startDate, endDate, delivery_date, status, purchased_flag, search } = {}) => Object.fromEntries(
  Object.entries({ page, pageSize, period, startDate, endDate, delivery_date, status, purchased_flag, search }).filter(([, value]) => value !== undefined && value !== null && value !== "")
);

const getOpsOrdersCacheKey = (options) => JSON.stringify(getOpsOrdersParams(options));

const normalizeOpsOrdersResponse = (response, params) => {
  const campaigns = Array.isArray(response?.data) ? response.data.map((campaign) => ({
    ...campaign,
    orders: Array.isArray(campaign.orders) ? campaign.orders : [],
  })) : [];
  campaigns.forEach((campaign) => campaign.orders.forEach((order) => {
    if (!order.user_id) return;
    const accountKind = String(order.user_type || "").toLowerCase().includes("guest") ? "guest" : "registered";
    storeAdminUserSnapshot({
      user_id: order.user_id,
      contact_name: order.contact_name,
      email: order.email,
      phone: order.contact_phone,
      role: order.user_type || "buyer",
      last_active: order.created_at,
      last_order_date: order.created_at,
      total_orders: 1,
      total_amount_spent: order.total_amount_paid,
      address: order.delivery_location,
    }, accountKind);
  }));
  latestOpsCampaigns = campaigns;

  return {
    status: Boolean(response?.status),
    message: response?.message,
    pagination: {
      total_items: Number(response?.pagination?.total_items ?? 0),
      total_orders_count: Number(response?.pagination?.total_orders_count ?? 0),
      total_purchased_orders: Number(response?.pagination?.total_purchased_orders ?? 0),
      total_fulfilled_orders: Number(response?.pagination?.total_fulfilled_orders ?? 0),
      current_page: Number(response?.pagination?.current_page ?? params.page),
      total_pages: Number(response?.pagination?.total_pages ?? 1),
      pageSize: Number(response?.pagination?.pageSize ?? params.pageSize),
    },
    data: campaigns,
  };
};

const getOpsOrdersByUsersParams = ({
  page = 1,
  pageSize = 10,
  fulfillment_status,
  delivery_date,
  state,
  sortBy = "delivery_date",
  sortOrder = "asc",
} = {}) => Object.fromEntries(Object.entries({
    page,
    pageSize,
    fulfillment_status,
    delivery_date,
    state,
    sortBy,
    sortOrder,
  }).filter(([, value]) => value !== undefined && value !== null && value !== ""));

export const getOpsOrdersByUsers = (options = {}) => {
  const params = getOpsOrdersByUsersParams(options);
  const { page = 1, pageSize = 10 } = options;

  return adminAxiosInstance.get("/admin/internal/ops/orders/users", { params }).then(({ data: response }) => {
  const pagination = response?.pagination || {};

  return {
    status: Boolean(response?.status),
    message: response?.message,
    data: Array.isArray(response?.data) ? response.data : [],
    pagination: {
      total_users: Number(pagination.total_users ?? 0),
      total_orders_count: Number(pagination.total_orders_count ?? 0),
      total_purchased_orders: Number(pagination.total_purchased_orders ?? 0),
      total_fulfilled_orders: Number(pagination.total_fulfilled_orders ?? 0),
      current_page: Number(pagination.current_page ?? page),
      total_pages: Number(pagination.total_pages ?? 1),
      pageSize: Number(pagination.pageSize ?? pageSize),
    },
  };
  });
};

export const getOrderRatings = async ({ page = 1, limit = 20, days = "allTime" } = {}) => {
  const { data: response } = await adminAxiosInstance.get("/admin/internal/ops/orders/ratings", {
    params: { page, limit, days },
  });

  if (!response?.status) throw new Error(response?.message || "Order ratings could not be loaded.");

  const pagination = response.pagination || {};
  return {
    data: Array.isArray(response.data) ? response.data : [],
    summary: response.summary || {},
    pagination: {
      total_items: Number(pagination.total_items ?? 0),
      total_pages: Number(pagination.total_pages ?? 1),
      current_page: Number(pagination.current_page ?? page),
      limit: Number(pagination.limit ?? limit),
    },
  };
};

export const exportOpsOrdersCsv = async (options = {}) => {
  const { data, headers } = await adminAxiosInstance.get("/admin/internal/ops/orders/export-csv", {
    params: getOpsOrdersByUsersParams(options),
    responseType: "blob",
  });
  return { blob: data, contentDisposition: headers?.["content-disposition"] || "" };
};

const fetchOpsOrders = (options) => {
  const params = getOpsOrdersParams(options);
  const cacheKey = getOpsOrdersCacheKey(options);
  if (opsOrdersRequests.has(cacheKey)) return opsOrdersRequests.get(cacheKey);

  const request = adminAxiosInstance.get("/admin/internal/ops/orders/split-buys", { params })
    .then(({ data }) => {
      const result = normalizeOpsOrdersResponse(data, params);
      opsOrdersCache.set(cacheKey, { data: result, updatedAt: Date.now() });
      return result;
    })
    .finally(() => opsOrdersRequests.delete(cacheKey));
  opsOrdersRequests.set(cacheKey, request);
  return request;
};

export const getCachedOpsOrders = (options = {}) => {
  const cached = opsOrdersCache.get(getOpsOrdersCacheKey(options));
  return cached && Date.now() - cached.updatedAt < OPS_ORDERS_STALE_TTL ? cached.data : null;
};

export const clearOpsOrdersCache = () => {
  opsOrdersCache.clear();
  opsOrdersRequests.clear();
  latestOpsCampaigns = [];
  if (typeof window !== "undefined") window.sessionStorage.removeItem(OPS_ORDER_SESSION_KEY);
};

const updateOpsOrdersCache = (updater) => {
  opsOrdersCache.forEach((entry, key) => {
    const data = { ...entry.data, data: updater(entry.data.data) };
    opsOrdersCache.set(key, { ...entry, data });
  });
  latestOpsCampaigns = updater(latestOpsCampaigns);
};

export const getOpsOrders = async (options = {}) => {
  const cacheKey = getOpsOrdersCacheKey(options);
  const cached = opsOrdersCache.get(cacheKey);
  const age = cached ? Date.now() - cached.updatedAt : Infinity;

  if (cached && age < OPS_ORDERS_FRESH_TTL) {
    latestOpsCampaigns = cached.data.data;
    return { ...cached.data, cacheStatus: "fresh" };
  }

  if (cached && age < OPS_ORDERS_STALE_TTL) {
    latestOpsCampaigns = cached.data.data;
    fetchOpsOrders(options).catch(() => undefined);
    return { ...cached.data, cacheStatus: "stale" };
  }

  return fetchOpsOrders(options);
};

export const getOpsOrderById = async (orderId) => {
  const campaign = latestOpsCampaigns.find((item) => item.orders.some((order) => order.order_id === orderId));
  const order = campaign?.orders.find((item) => item.order_id === orderId);

  const splitBuy = { ...(campaign || {}) };
  delete splitBuy.orders;

  if (order) {
    const orderDetails = {
      ...order,
      split_buy: splitBuy,
    };
    storeOpsOrderSnapshot(orderDetails);
    return { status: true, data: orderDetails };
  }

  const storedOrder = readOpsOrderSnapshot(orderId);
  if (storedOrder) return { status: true, data: storedOrder, cacheStatus: "session" };

  try {
    const { data: response } = await adminAxiosInstance.get(
      `/admin/internal/ops/orders/${encodeURIComponent(orderId)}`
    );
    const orderDetails = response?.data || response?.order;
    if (response?.status && orderDetails) {
      storeOpsOrderSnapshot(orderDetails);
      return { ...response, data: orderDetails };
    }
  } catch (error) {
    if (error?.response?.status !== 404) throw error;
  }

  return { status: false, message: "Order transaction not found in the fetched orders", data: null };
};

export const flagSplitBuyPurchased = async (splitBuyId) => {
  const { data: response } = await adminAxiosInstance.patch(
    `/admin/internal/ops/split-buy/${encodeURIComponent(splitBuyId)}/flag-purchased`
  );
  if (!response?.status) throw new Error(response?.message || "The Split-Buy could not be marked as purchased.");

  updateOpsOrdersCache((campaigns) => campaigns.map((campaign) => campaign.split_buy_id === splitBuyId ? {
    ...campaign,
    purchased_flag: Boolean(response.purchased_flag),
    purchased_at: response.purchased_at,
    orders: campaign.orders.map((order) => ({ ...order, fulfillment_status: order.fulfillment_status === "pending" ? "purchased" : order.fulfillment_status })),
  } : campaign));
  opsOrdersCache.forEach((_, key) => {
    if (JSON.parse(key).purchased_flag !== undefined) opsOrdersCache.delete(key);
  });
  return response;
};

export const updateOrderFulfillment = async (orderId, fulfillmentStatus, orderNotes) => {
  const { data: response } = await adminAxiosInstance.patch(
    `/admin/internal/ops/orders/${encodeURIComponent(orderId)}/fulfillment`,
    {
      fulfillment_status: fulfillmentStatus,
      order_notes: orderNotes,
    }
  );
  if (!response?.status || !response?.order) {
    throw new Error(response?.message || "The order fulfilment update could not be saved.");
  }

  updateOpsOrdersCache((campaigns) => campaigns.map((campaign) => ({
    ...campaign,
    orders: campaign.orders.map((order) => order.order_id === orderId ? {
      ...order,
      ...response.order,
      fulfillment_history: [...(order.fulfillment_history || []), ...response.order.fulfillment_history],
    } : order),
  })));
  const storedOrder = readOpsOrderSnapshot(orderId);
  if (storedOrder) {
    storeOpsOrderSnapshot({
      ...storedOrder,
      ...response.order,
      fulfillment_history: [...(storedOrder.fulfillment_history || []), ...response.order.fulfillment_history],
    });
  }
  return response;
};

export const updateUserDeliveryFulfillment = async ({ user_id, delivery_date, fulfillment_status, order_notes, send_notification }) => {
  const { data: response } = await adminAxiosInstance.patch(
    "/admin/internal/ops/orders/user-fulfillment",
    { user_id, delivery_date, fulfillment_status, order_notes, send_notification }
  );
  if (!response?.status) throw new Error(response?.message || "The delivery group could not be updated.");
  return response;
};
